/**
 * Centralized React Query keys for rr-admin.
 * Keep keys here so cache invalidation across modules stays consistent.
 */
export type QueryFilters = Record<string, unknown>;

// ── Platform (admin-only — companies, users, roles, dashboard) ────────────────

const platform = {
  root: ["platform"] as const,
  // Companies
  companies: () => [...platform.root, "companies"] as const,
  companiesList: (filters: QueryFilters) =>
    [...platform.companies(), "list", filters] as const,
  company: (id: string) => [...platform.companies(), "detail", id] as const,
  companyMetrics: (id: string) =>
    [...platform.company(id), "metrics"] as const,
  companyDepartments: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "departments", filters] as const,
  companyUsers: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "users", filters] as const,
  companyVendors: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "vendors", filters] as const,
  companyCurrencies: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "currencies", filters] as const,
  companyContacts: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "contacts", filters] as const,
  companyCustomers: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "customers", filters] as const,
  companyRegions: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "regions", filters] as const,
  companyQuoteSources: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "quote-sources", filters] as const,
  companyClientTypes: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "client-types", filters] as const,
  companyEntityRatings: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "entity-ratings", filters] as const,
  companyRefusalReasons: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "refusal-reasons", filters] as const,
  companyContactPositions: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "contact-positions", filters] as const,
  companyCargos: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "cargos", filters] as const,
  companyQuotes: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "quotes", filters] as const,
  companyShipments: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "shipments", filters] as const,
  companyDrivers: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "drivers", filters] as const,
  companyVehicles: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "vehicles", filters] as const,
  companyCarriers: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "carriers", filters] as const,
  companyClients: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "clients", filters] as const,
  companyOrders: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "orders", filters] as const,
  companyAddresses: (companyId: string, filters: QueryFilters) =>
    [...platform.company(companyId), "addresses", filters] as const,
  // Users (platform admins)
  users: () => [...platform.root, "users"] as const,
  usersList: (filters: QueryFilters) =>
    [...platform.users(), "list", filters] as const,
  user: (id: string) => [...platform.users(), "detail", id] as const,
  // Tenant users (CompanyUser) listed across orgs
  tenantUsers: () => [...platform.root, "tenant-users"] as const,
  tenantUsersList: (filters: QueryFilters) =>
    [...platform.tenantUsers(), "list", filters] as const,
  tenantUserSessions: (userId: string) =>
    [...platform.tenantUsers(), "sessions", userId] as const,
  companySessions: (companyId: string) =>
    [...platform.company(companyId), "sessions"] as const,
  // Roles & permissions
  roles: () => [...platform.root, "roles"] as const,
  rolesList: (filters: QueryFilters) =>
    [...platform.roles(), "list", filters] as const,
  permissions: () => [...platform.root, "permissions"] as const,
  permissionsList: (filters: QueryFilters) =>
    [...platform.permissions(), "list", filters] as const,
  // Reference data
  reference: () => [...platform.root, "reference"] as const,
  referenceProfile: (profile: string) =>
    [...platform.root, "reference", profile] as const,
  companyReference: () => [...platform.root, "company-reference"] as const,
  // Dashboard
  stats: () => [...platform.root, "stats"] as const,
  // Broadcasts (marketing announcements)
  broadcasts: () => [...platform.root, "broadcasts"] as const,
  broadcastAudienceCounts: () =>
    [...platform.root, "broadcasts", "audience-counts"] as const,
};

// ── Cross-resource keys mirrored from tenant-web (admin can read these) ────

const tenant = {
  root: ["tenant"] as const,
  departments: () => [...tenant.root, "departments"] as const,
  clientTypes: () => [...tenant.root, "client-types"] as const,
  ratings: () => [...tenant.root, "ratings"] as const,
  currencies: () => [...tenant.root, "currencies"] as const,
  vatRates: () => [...tenant.root, "vat-rates"] as const,
  users: () => [...tenant.root, "users"] as const,
};

const quotes = {
  root: ["quotes"] as const,
  lists: () => [...quotes.root, "list"] as const,
  list: (filters: QueryFilters) => [...quotes.lists(), filters] as const,
  detail: (id: string) => [...quotes.root, "detail", id] as const,
};

const orders = {
  root: ["orders"] as const,
  lists: () => [...orders.root, "list"] as const,
  list: (filters: QueryFilters) => [...orders.lists(), filters] as const,
  detail: (id: string) => [...orders.root, "detail", id] as const,
};

const clients = {
  root: ["clients"] as const,
  lists: () => [...clients.root, "list"] as const,
  list: (filters: QueryFilters) => [...clients.lists(), filters] as const,
  detail: (id: string) => [...clients.root, "detail", id] as const,
};

const carriers = {
  root: ["carriers"] as const,
  lists: () => [...carriers.root, "list"] as const,
  list: (filters: QueryFilters) => [...carriers.lists(), filters] as const,
  detail: (id: string) => [...carriers.root, "detail", id] as const,
};

const drivers = {
  root: ["drivers"] as const,
  lists: () => [...drivers.root, "list"] as const,
  list: (filters: QueryFilters) => [...drivers.lists(), filters] as const,
  detail: (id: string) => [...drivers.root, "detail", id] as const,
};

const vehicles = {
  root: ["vehicles"] as const,
  lists: () => [...vehicles.root, "list"] as const,
  list: (filters: QueryFilters) => [...vehicles.lists(), filters] as const,
  detail: (id: string) => [...vehicles.root, "detail", id] as const,
};

const calculations = {
  root: ["calculations"] as const,
  lists: () => [...calculations.root, "list"] as const,
  list: (filters: QueryFilters) => [...calculations.lists(), filters] as const,
};

const filterTemplates = {
  root: ["filter-templates"] as const,
  list: (context: string) => [...filterTemplates.root, "list", context] as const,
};

export const queryKeys = {
  platform,
  tenant,
  quotes,
  orders,
  clients,
  carriers,
  drivers,
  vehicles,
  calculations,
  filterTemplates,
};
